import { useState } from "react";
import { useIntl } from "react-intl";
import { useScroll } from "src/ScrollContext";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { DateRangeCalendar } from "src/components/DateRangeCalendar/DateRangeCalendar";
import Calendar from "src/design-system/icons/Calendar.svg?react";
import Passenger from "src/design-system/icons/User.svg?react";
import color from "src/design-system/tokens/color";
import { screenMinWidth } from "src/theme";

import type { CalendarDate } from "src/components/DatePicker/BaseCalendar";
import looseValueToDate from "src/components/DatePicker/looseValueToDate";
import { isDateRange } from "src/components/DatePicker/utils/datePredicates";
import { SearchButton as VariantSearchButton } from "src/components/SearchButton/SearchButton";
import { InputBar, InputBars } from "src/components/SearchInput/SearchInput";
import { Icon } from "src/design-system/components/Icon/Icon";
import { spacing } from "src/design-system/tokens/spacing";
import { headerMessages } from "src/domain/TopNav/TopNav.messages";
import { isSameDate, numDaysBetweenTwoDates } from "src/utils/dateTime";
import styled from "styled-components";
import {
  getDefaultCheckOutDate,
  type RoomDetail,
  useHotelsContext,
} from "../HotelsContext";
import type { HotelFetchUpdates } from "../HotelsScreen";
import { useHotelStayAndRoomDetailsText } from "../utils";
import { Combobox } from "./Combobox";
import { DestinationInput } from "./DestinationInput";
import messages from "./HotelSearch.messages";
import { RoomsForm } from "./RoomsForm/RoomsForm";
import { MAX_FUTURE_BOOKABLE_DAYS } from "./constants";
export type RequestTrigger = "DateChange" | "RoomDetailsChange";

export default function HotelLargeSearchBar() {
  const intl = useIntl();
  const {
    stayDatesUI,
    setStayDatesUI,
    initiateFakeHotelLoad,
    roomDetailsUI,
    setRoomDetailsUI,
    setHotelListQueryParams,
    filtersState,
    isDatePickerOpen,
    setIsDatePickerOpen,
    datePickerRef,
    setFocusToDatePicker,
    setShowDatesPopover,
  } = useHotelsContext();
  const { setScrollTop } = useScroll();

  // Date picker init
  const minDate = new Date();
  const { datesText, roomDetailsText } = useHotelStayAndRoomDetailsText();
  const [initialDate, setInitialDate] = useState<Date | undefined>(undefined);

  // Room details init
  const [isRoomDetailsExpanded, setIsRoomDetailsExpanded] = useState(false);
  const [freshFormData, setFreshFormData] =
    useState<RoomDetail[]>(roomDetailsUI);

  function handleApiSearch(trigger: RequestTrigger, dates?: [Date, Date]) {
    if (dates) {
      refetchHotelList({
        arrival: dates[0],
        departure: dates[1],
      });
    } else {
      refetchHotelList({
        roomDetails: freshFormData,
      });
    }
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Initiate:hotelListRequest",
      label: `${trigger}`,
    });
  }

  function refetchHotelList(updates: HotelFetchUpdates) {
    setHotelListQueryParams((state) => {
      return {
        ...state,
        arrivalDate: updates.arrival ?? stayDatesUI?.[0],
        departureDate: updates.departure ?? stayDatesUI?.[1],
        roomDetails: updates.roomDetails ?? roomDetailsUI,
        filters: filtersState,
      };
    });
  }

  function handleDateRangeSelected(jsDates: CalendarDate) {
    if (!isDateRange(jsDates)) {
      return;
    }

    const startDate = looseValueToDate(jsDates[0]);
    let endDate = looseValueToDate(jsDates[1]);

    if (!startDate) {
      return;
    }

    setShowDatesPopover(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilDate = numDaysBetweenTwoDates(today, startDate);
    let gaLabelSuffix = "-dateRangeSelected";

    // If the user has only selected a check-in date, then we don't want to update the stayDatesUI yet.
    if (!endDate) {
      setInitialDate(startDate);
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Click:HotelDateRangeCalendar",
        label: `firstDate`,
      });
      return;
    }

    // Auto selects the default date range if same date clicked.
    if (isSameDate(startDate, endDate)) {
      endDate = getDefaultCheckOutDate(endDate);
      gaLabelSuffix = `${gaLabelSuffix}-sameDateClick`;
    }

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:HotelDateRangeCalendar",
      label: `daysInAdvance:${daysUntilDate}${gaLabelSuffix}`,
    });

    const dateRange: [Date, Date] = [startDate, endDate];
    setStayDatesUI(dateRange);
    handleApiSearch("DateChange", dateRange);
    setIsDatePickerOpen(false);
    setInitialDate(undefined);
    setScrollTop(0);
  }

  function handleDatePickerBackdropClick() {
    if (initialDate) {
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Click:HotelDateRangeCalendar",
        label: `backdrop`,
      });

      const assumedSecondDate = new Date(initialDate);
      assumedSecondDate.setDate(initialDate.getDate() + 1);

      const assumedDateRange: [Date, Date] = [initialDate, assumedSecondDate];
      handleDateRangeSelected(assumedDateRange);
    } else {
      // Date picker manually closed with backdrop click -> no date selection.
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Dismiss:datePicker",
        label: `noSelection-backdrop`,
      });
    }
  }

  function handleRoomDetailsFormSubmit() {
    const sumAdults = roomDetailsUI.reduce((sum, room) => sum + room.adults, 0);
    const sumChildren = roomDetailsUI.reduce(
      (sum, room) => sum + room.children.length,
      0,
    );
    const sumRooms = roomDetailsUI.length;

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:RoomsDetailsFormSearch",
      label: `adults${sumAdults}:children${sumChildren}:rooms${sumRooms}`,
    });

    setRoomDetailsUI(freshFormData);
    handleApiSearch("RoomDetailsChange");
    setIsRoomDetailsExpanded(false);
    setScrollTop(0);
  }

  function handleSearch() {
    initiateFakeHotelLoad();
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:search-button",
      label: "no-request",
    });
  }

  function resetRoomDetailsForm() {
    setFreshFormData(roomDetailsUI); // reset form to what was in context previously.
  }

  return (
    <InputBars data-testid="hotels-search-bar">
      <StyledInputBar>
        <DestinationInput setFocusToDatePicker={setFocusToDatePicker} />
        <VerticalDivider />
        <StyledComboBox
          id="hotel-search-dates-combobox"
          icon={
            <Icon>
              <Calendar
                aria-label={intl.formatMessage(headerMessages.when)}
                className="text-icon-primary"
              />
            </Icon>
          }
          label={intl.formatMessage(messages.checkInCheckOutLabel)}
          ariaLabel={intl.formatMessage(messages.datesAriaLabel)}
          inputText={datesText}
          isDoubleViewCalendar={true}
          isChildExpanded={isDatePickerOpen}
          setIsChildExpanded={(expanded) => {
            setIsDatePickerOpen(expanded);
          }}
          onBackgroundClick={handleDatePickerBackdropClick}
          focusRef={datePickerRef}
          onClickOpen={() => {
            sendAnalyticsInteractionEvent({
              category: "Hotels",
              action: "Click:hotels-dates",
              label: "open",
            });
          }}
          hotelInput
          anchor="center"
          dropdownBorderRadius="rounded_xl"
        >
          <DateRangeCalendar
            key={isDatePickerOpen.toString()}
            allowPartialRange
            selectedDates={initialDate ?? stayDatesUI}
            setDateRange={handleDateRangeSelected}
            minDate={minDate}
            maxDaysAhead={MAX_FUTURE_BOOKABLE_DAYS}
            showDoubleView={true}
          />
        </StyledComboBox>
        <VerticalDivider />
        <StyledComboBox
          id="hotel-search-room-details-combobox"
          icon={
            <Icon>
              <Passenger
                aria-label={intl.formatMessage(headerMessages.who)}
                className="text-icon-primary"
              />
            </Icon>
          }
          label={intl.formatMessage(messages.guestsLabel)}
          ariaLabel={intl.formatMessage(messages.roomDetailsAriaLabel)}
          inputText={roomDetailsText}
          onClickCta={handleRoomDetailsFormSubmit}
          ctaMessage={intl.formatMessage(messages.done)}
          isChildExpanded={isRoomDetailsExpanded}
          setIsChildExpanded={setIsRoomDetailsExpanded}
          showMobileControls={true}
          onBackgroundClick={resetRoomDetailsForm}
          onDismissClick={resetRoomDetailsForm}
          onClickOpen={() => {
            sendAnalyticsInteractionEvent({
              category: "Hotels",
              action: "Click:hotels-travellers-form",
              label: "open",
            });
          }}
          hotelInput
          dropdownBorderRadius="rounded_xl"
        >
          <RoomsForm
            freshFormData={freshFormData}
            setFreshFormData={setFreshFormData}
            onCtaClick={handleRoomDetailsFormSubmit}
            ctaMessage={intl.formatMessage(messages.done)}
          />
        </StyledComboBox>
      </StyledInputBar>

      <VariantSearchButton onPress={handleSearch} />
    </InputBars>
  );
}

const StyledComboBox = styled(Combobox)`
  ${screenMinWidth.sm} {
    flex: 1;
  }
`;

const StyledInputBar = styled(InputBar)`
  height: ${spacing.xxxxl};
`;

const VerticalDivider = styled.div`
  height: 20px;
  width: 1px;
  background-color: ${color.primitive.neutralAlpha800};
`;
